import { Tag } from "primereact/tag";
import {
  EngineDisagreement,
  WorklistState,
} from "types/BenefitComparison/BenefitComparison";

import {
  ENGINE_DISAGREEMENT_LABELS,
  ENGINE_DISAGREEMENT_TAGS,
  WORKLIST_STATE_LABELS,
  WORKLIST_STATE_SEVERITY,
} from "./benefitComparisonWording";

/**
 * The two states that are NOT "some source disagrees" and so still earn a tag of their own.
 *
 * 🚨 EVERY OTHER STATE IS SAID BY THE SOURCE CHIPS, and said better. A cluster has ONE state — the
 * first rule `worklistStateOf` matched, remit outranking legacy outranking the AI — but up to FOUR
 * disagreements, so as a label under a source filter it could only mislead: filtering "Legacy
 * disagrees" returned a page of rows every one of which was tagged "Remits disagree", because that
 * is what the higher-priority rule matched first. The priority still earns its keep in
 * `rankWorklist`, which is what it was written for.
 *
 * A regression is a different KIND of fact ("this used to match and no longer does") and an
 * unconfigured account has no comparison at all, so neither is expressible as a set of sources.
 */
export const FLAG_STATES: WorklistState[] = ["REGRESSED", "NOT_CONFIGURED"];

/**
 * Which sources the engine differs from, as chips, over the flag tag where there is one.
 *
 * 🚨 ONE COMPONENT FOR THE TABLE AND THE CLUSTER HEADER. Both used to render the single State tag
 * and both were wrong in the same way; rendering them from one place is what stops the next change
 * fixing only the half somebody was looking at.
 */
export function EngineDisagreesWith({
  state,
  sources,
  testId,
}: {
  state: WorklistState | undefined;
  sources: EngineDisagreement[];
  testId: string;
}) {
  const flagged = state !== undefined && FLAG_STATES.includes(state);
  return (
    <div className="flex flex-wrap align-items-center gap-1">
      {flagged && state && (
        <Tag
          severity={WORKLIST_STATE_SEVERITY[state]}
          value={WORKLIST_STATE_LABELS[state]}
          className="white-space-nowrap"
          data-testid={`benefitWorklist.state.${state}`}
        />
      )}
      {sources.length > 0 ? (
        <span
          className="flex flex-wrap align-items-center gap-1"
          data-testid={testId}
        >
          {sources.map((source) => (
            <Tag
              key={source}
              value={ENGINE_DISAGREEMENT_LABELS[source]}
              className="white-space-nowrap"
              {...ENGINE_DISAGREEMENT_TAGS[source]}
            />
          ))}
        </span>
      ) : (
        !flagged && <span className="text-color-secondary">—</span>
      )}
    </div>
  );
}
